<template>
  <div>
    <form
      v-bind:class="{
        'o-form': true,
        'o-form--sidebar-program-search': isOnResultsPage,
      }"
    >
      <fieldset class="o-form__section s-content-block s-content-block--small">
        <h4 v-if="isOnResultsPage">Adult Basic Education Transfer Search</h4>
        <div v-if="!isOnResultsPage">
          <!-- <p>
            Adult Basic Education (ABE) includes a number of programs to help
            adults develop basic skills, obtain a secondary school graduation
            diploma, and obtain vocational training. Online information about ABE 
            is provided by the Ministry of Education and the Ministry of Advanced 
            Education and Skills Training.
          </p>
          <p>
            Use the course search below to find equivalencies amongst ABE
            programs in BC from 2020 onward. To find more information about the ABE
            program, including the full Articulation Guide as well as listings from 
            prior to 2020, please contact <a href='mailto:bctransferguide@bccat.ca'>bctransferguide@bccat.ca</a>.
          </p> -->
          <div v-html="searchHeaderContent"></div>

          <br />
          <h4>Find courses for ABE transfer:</h4>
        </div>

        <div class="grid-x grid-padding-y">
          <!-- Discipline -->
          <div
            v-bind:class="{
              cell: true,
              'small-12': true,
              'medium-6': !isOnResultsPage,
            }"
          >
            <div class="m-field-group m-field-group--contain">
              <label for="">Discipline</label>
              <div v-for="discipline in disciplines" :key="discipline.id">
                <input
                  type="checkbox"
                  :id="`discipline_checkbox_${discipline.id}`"
                  :value="discipline.id"
                  v-model="discipline.checked"
                  @click="anyDisciplineChecked = false"
                  @change="disciplineSelected"
                />
                <label :for="`discipline_checkbox_${discipline.id}`"> {{ discipline.name }}</label
                ><br />
              </div>
              <input
                type="checkbox"
                :id="`discipline_checkbox_any`"
                v-model="anyDisciplineChecked"
                @click="anyDisciplineClicked"
                v-on:input="$event.target.checked = true"
              />
              <label :for="`discipline_checkbox_any`"> any</label><br />
            </div>
          </div>

          <!-- Level -->
          <div class="cell small-12 medium-6">
            <div class="m-field-group m-field-group--contain">
              <label for="">Level</label>
              <div v-for="level in levels" :key="level.id">
                <input
                  type="checkbox"
                  :id="`level_checkbox_${level.id}`"
                  :value="level.id"
                  v-model="level.checked"
                  @click="anyLevelChecked = false"
                  @change="levelSelected"
                />
                <label :for="`level_checkbox_${level.id}`"> {{ level.name }}</label
                ><br />
              </div>
              <input
                type="checkbox"
                :id="`level_checkbox_any`"
                v-model="anyLevelChecked"
                @click="anyLevelClicked"
                v-on:input="$event.target.checked = true"
              />
              <label :for="`level_checkbox_any`"> any</label><br />
            </div>
          </div>
        </div>

        <!-- Institution -->
        <div class="m-field-group m-field-group--contain">
          <label for="">Offering Institution</label>
          <select v-model="filter.institutionId">
            <option :value="null" selected>All Institutions</option>
            <option
              v-for="institution in institutions"
              :key="institution.id"
              :value="institution.id"
            >
              {{ institution.code }} - {{ institution.title }}
            </option>
          </select>
        </div>

        <!-- Year -->
        <div class="m-field-group m-field-group--contain">
          <label for="">Academic Year</label>
          <select v-model="filter.academicYear">
            <option :value="null">All Years (2019 onwards)</option>
            <option v-for="year in years" :key="year.year" :value="year.year">
              {{ year.label }}
            </option>
          </select>
        </div>

        <!-- Course Search -->
        <div class="m-field-group m-field-group--contain">
          <label for="course_search_input">Course Search</label>
          <input
            id="course_search_input"
            type="text"
            v-model="filter.searchText"
            maxlength="10"
            @input="trimInput"
          />
        </div>
      </fieldset>

      <button type="button" class="a-button" @click="submitSearch">Search</button>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, watch, onMounted } from 'vue'
import { defineProps, defineEmits, defineExpose } from 'vue'
import axios from 'axios'
import _ from 'lodash'

const adultEducationTransferSearchFilterDataStorageName = 'adult-education-transfer-search-params'

const props = defineProps({
  nonce: { type: String, required: true },
  basePagePath: { type: String, default: false },
  resultsPagePath: { type: String, default: false },
  isOnResultsPage: { type: Boolean, default: false },
  searchHeaderContent: { type: String, required: true },
  apiUrls: { type: Object, required: true },
})

const emit = defineEmits(['onSubmit'])

const filterDataLocalStorage = sessionStorage.getItem(
  adultEducationTransferSearchFilterDataStorageName,
)

const filter = reactive({
  disciplines: null,
  levels: null,
  institutionId: null,
  academicYear: _.isString(filterDataLocalStorage)
    ? JSON.parse(filterDataLocalStorage).academicYear
    : new Date().getMonth() >= 5
      ? new Date().getFullYear()
      : new Date().getFullYear() - 1,
  searchText: '',
})

if (_.isString(filterDataLocalStorage)) {
  const parsedData = JSON.parse(filterDataLocalStorage)
  Object.assign(filter, parsedData)
}

const disciplines = ref([])
const levels = ref([])
const institutions = ref([])
const years = ref([])
const anyDisciplineChecked = ref(false)
const anyLevelChecked = ref(false)

const getDisciplines = () => {
  axios.get(props.apiUrls.searchBaseURL + 'abe/GetDisciplines').then((response) => {
    response.data = _.sortBy(response.data, 'name')
    disciplines.value = _.map(response.data, (discipline) => {
      discipline.checked = false

      if (_.get(filter, 'disciplines.length')) {
        discipline.checked = filter.disciplines.includes(discipline.id)
      }

      return discipline
    })

    if (!_.get(filter, 'disciplines.length')) {
      anyDisciplineChecked.value = true
    }
  })
}

const getLevels = () => {
  axios.get(props.apiUrls.searchBaseURL + 'abe/GetLevels').then((response) => {
    response.data = _.sortBy(response.data, 'id')
    levels.value = _.map(response.data, (level) => {
      level.checked = false

      if (_.get(filter, 'levels.length')) {
        level.checked = filter.levels.includes(level.id)
      }

      return level
    })

    if (!_.get(filter, 'levels.length')) {
      anyLevelChecked.value = true
    }
  })
}

const getInstitutions = () => {
  axios.get(props.apiUrls.searchBaseURL + 'abe/GetInstitutions').then((response) => {
    institutions.value = _.sortBy(response.data, 'title')
  })
}

const getYearsSelectOptionData = () => {
  const startYear = 2019
  const currentYear =
    new Date().getMonth() >= 5 ? new Date().getFullYear() : new Date().getFullYear() - 1
  const years = []

  for (let i = currentYear; i >= startYear; i--) {
    years.push({
      year: i,
      label: `${i}/` + _.toString(i + 1).slice(-2),
    })
  }

  return years
}

const getFilterData = () => {
  const filterData = _.clone(filter)

  filterData.disciplines = getCheckedDisciplines()
  filterData.levels = getCheckedLevels()

  return filterData
}

const updatePersistence = () => {
  const data = getFilterData()

  sessionStorage.setItem(adultEducationTransferSearchFilterDataStorageName, JSON.stringify(data))
}

const clearPersistence = () => {
  sessionStorage.removeItem(adultEducationTransferSearchFilterDataStorageName)
}

const newSearch = () => {
  clearPersistence()
  window.location.href = props.basePagePath
}

const submitSearch = () => {
  updatePersistence()

  if (!props.isOnResultsPage) {
    window.location = props.resultsPagePath
    return
  }

  submit()
}

const submit = (filterData) => {
  if (!filterData) {
    filterData = getFilterData()
  }

  filterData.searchText = filterData.searchText.trim()
  emit('onSubmit', filterData)
}

const trimInput = (event) => {
  filter.searchText = sanitizeInput(event.target.value)
}

const sanitizeInput = (input) => {
  return input.replace(/[\0\x08\x09\x1a\n\r"'\\\%]/g, function (char) {
    switch (char) {
      case '\0':
        return '\\0'
      case '\x08':
        return '\\b'
      case '\x09':
        return '\\t'
      case '\x1a':
        return '\\z'
      case '\n':
        return '\\n'
      case '\r':
        return '\\r'
      case '"':
      case "'":
      case '\\':
      case '%':
        return '\\' + char
    }
  })
}

const disciplineSelected = () => {
  const checked = disciplines.value.filter((l) => l.checked)

  if (checked.length === 0) {
    anyDisciplineChecked.value = true
  }
}

const levelSelected = () => {
  const checked = levels.value.filter((l) => l.checked)

  if (checked.length === 0) {
    anyLevelChecked.value = true
  }
}

const anyDisciplineClicked = (event) => {
  if (event.target.checked) {
    _.forEach(disciplines.value, (discipline, index) => {
      _.set(disciplines.value, [index, 'checked'], false)
    })
  }

  updatePersistence()
}

const anyLevelClicked = (event) => {
  if (event.target.checked) {
    _.forEach(levels.value, (level, index) => {
      _.set(levels.value, [index, 'checked'], false)
    })
  }

  updatePersistence()
}

const getCheckedDisciplines = () => {
  if (anyDisciplineChecked.value) {
    return null
  }

  const checked = _.filter(disciplines.value, (discipline) => discipline.checked)

  return _.map(checked, (discipline) => discipline.id)
}

const getCheckedLevels = () => {
  if (anyLevelChecked.value) {
    return null
  }

  const checked = _.filter(levels.value, (level) => level.checked)

  return _.map(checked, (level) => level.id)
}

watch(filter, updatePersistence, { deep: true })
watch(disciplines, updatePersistence, { deep: true })
watch(levels, updatePersistence, { deep: true })

onMounted(() => {
  getDisciplines()
  getLevels()
  getInstitutions()
  years.value = getYearsSelectOptionData()

  if (props.isOnResultsPage) {
    submit(filter)
  }
})

defineExpose({
  newSearch,
})
</script>
